import { Component,  OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import { Observable,  Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    TypeOfTitle, TypeOfTitleOnServerCreated, TypeOfTitleUpdated, selectTypeOfTitleById,
    selectTypeOfTitlesActionLoading, selectLastCreatedTypeOfTitleId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-type-of-title-edit',
    templateUrl: './type-of-title-edit.component.html',
    styleUrls: ['./type-of-title-edit.component.scss']
})


export class TypeOfTitleEditComponent implements OnInit, OnDestroy {

    model: TypeOfTitle;
    oldModel: TypeOfTitle;

    typeOfTitleForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private routeSubscription: Subscription;

    /**
     * Component constructor
     *
     * @param activatedRoute
     * @param router
     * @param fb: FormBuilder
     * @param location
     * @param subheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectTypeOfTitlesActionLoading));
        this.routeSubscription = this.activatedRoute.params.subscribe(params => {
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectTypeOfTitleById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initTypeOfTitle();
                    }
                });
            } else {
                this.model = new TypeOfTitle();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initTypeOfTitle();
            }
        });

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Type Of Titles', page: `../admin-management/linked-tables/state-repairs`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/state-repairs/edit/${id}`
                    : `../admin-management/linked-tables/state-repairs/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    initTypeOfTitle() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.typeOfTitleForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Type Of Title '${
                this.model.name
                }'`;
        }

        return 'New Type Of Title';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.typeOfTitleForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.typeOfTitleForm.markAsPristine();
        this.typeOfTitleForm.markAsUntouched();
        this.typeOfTitleForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.typeOfTitleForm.controls;
        /** check form */
        if (this.typeOfTitleForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateTypeOfTitle(this.model);
        } else {
            this.createTypeOfTitle(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: TypeOfTitleModel
     */
    updateTypeOfTitle(_model: TypeOfTitle) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateTypeOfTitle: Update<TypeOfTitle> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new TypeOfTitleUpdated({
            partialTypeOfTitle: updateTypeOfTitle,
            typeOfTitle: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: TypeOfTitleModel
     */
    createTypeOfTitle(_model: TypeOfTitle) {
        this.store.dispatch(new TypeOfTitleOnServerCreated({typeOfTitle: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedTypeOfTitleId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
